import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import apiManager from '../lib/api/index';
import { trackSignedIn, trackSignedOut } from '../lib/zaraz/zarazUtils';

// Verificación de si estamos en el navegador o en el servidor
const isBrowser = typeof window !== 'undefined';

// Definir tipos para el usuario
export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  tarifa?: number;
  discount?: number;
  prontoPagoDiscount?: number;
}

// Definir tipos para el estado de autenticación
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
}

// Definir tipos para el contexto
export interface AuthContextType {
  auth: AuthState;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Estado inicial de autenticación
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: isBrowser, // Solo mostrar carga en el navegador
  error: null,
  initialized: !isBrowser, // En SSR se considera inicializado
};

// Proveedor del contexto
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [auth, setAuth] = useState<AuthState>(initialState);

  useEffect(() => {
    // Solo inicializar la autenticación en el navegador
    if (!isBrowser) return;
    
    // Inicializar el auth desde localStorage a través de la API
    const initAuth = async () => {
      try {
        // Intentar restaurar la sesión desde el localStorage
        if (apiManager.auth.restoreSession()) {
          // Asegurar que el token esté sincronizado con el cliente API
          const token = apiManager.client.getAuthToken();
          if (token) {
            apiManager.client.setAuthToken(token);
          }
          
          // Primero intentar obtener el usuario almacenado en localStorage
          const storedUser = apiManager.auth.getStoredUser();
          
          if (storedUser) {
            // Si hay un usuario almacenado en localStorage, usarlo temporalmente
            setAuth({
              isAuthenticated: true,
              user: storedUser,
              token: token,
              isLoading: true,
              error: null,
              initialized: false,
            });
            
            // Luego verificar con el backend que la sesión sea válida
            try {
              const response = await apiManager.auth.getProfile();
              
              if (response.success && response.data) {
                // Si la sesión es válida, actualizar con datos frescos
                setAuth({
                  isAuthenticated: true,
                  user: response.data,
                  token: apiManager.client.getAuthToken() || null,
                  isLoading: false,
                  error: null,
                  initialized: true,
                });
                
                // Actualizar el usuario almacenado con datos frescos
                localStorage.setItem('user', JSON.stringify(response.data));
              } else {
                // Si la sesión no es válida, limpiar todo
                console.error('Sesión inválida o expirada');
                apiManager.auth.logout();
                setAuth({
                  ...initialState,
                  isLoading: false,
                  initialized: true,
                });
              }
            } catch (error) {
              console.error('Error al verificar sesión:', error);
              // Mantener la sesión con el usuario almacenado si hay un error de red
              // pero marcarlo como inicializado
              setAuth(prev => ({
                ...prev,
                isLoading: false,
                initialized: true,
              }));
            }
          } else {
            // Si no hay usuario en localStorage, pero sí hay token, intentar obtener el perfil
            try {
              const response = await apiManager.auth.getProfile();
              
              if (response.success && response.data) {
                // Actualizar con el perfil obtenido
                setAuth({
                  isAuthenticated: true,
                  user: response.data,
                  token: apiManager.client.getAuthToken() || null,
                  isLoading: false,
                  error: null,
                  initialized: true,
                });
                
                // Guardar el usuario en localStorage
                localStorage.setItem('user', JSON.stringify(response.data));
              } else {
                // Si no se pudo obtener el perfil, limpiar todo
                apiManager.auth.logout();
                setAuth({
                  ...initialState,
                  isLoading: false,
                  initialized: true,
                });
              }
            } catch (error) {
              console.error('Error al obtener perfil:', error);
              apiManager.auth.logout();
              setAuth({
                ...initialState,
                isLoading: false,
                error: 'Error al cargar la sesión del usuario',
                initialized: true,
              });
            }
          }
        } else {
          // Si no hay token, simplemente marcar como inicializado y no autenticado
          setAuth({
            ...initialState,
            isLoading: false,
            initialized: true,
          });
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error);
        setAuth({
          ...initialState,
          isLoading: false,
          error: 'Error al cargar la sesión del usuario',
          initialized: true,
        });
      }
    };

    initAuth();
  }, []);
  
  // Función para iniciar sesión (solo funciona en el navegador)
  const login = async (email: string, password: string) => {
    if (!isBrowser) return;

    setAuth(prev => ({ ...prev, isLoading: true, error: null }));

    // Intentar login con reintentos en caso de error de red
    const maxRetries = 2;
    let lastError: string | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiManager.auth.login({ email, password });

        if (response.success && response.data) {
          // Sincronizar el token con el cliente API
          apiManager.client.setAuthToken(response.data.token);

          // Track signed in event
          trackSignedIn({
            user_id: response.data.user.id,
            email: response.data.user.email,
            name: response.data.user.name,
            role: response.data.user.role
          });

          setAuth({
            isAuthenticated: true,
            user: response.data.user,
            token: response.data.token,
            isLoading: false,
            error: null,
            initialized: true,
          });
          return; // Login exitoso, salir
        } else {
          // Error de la API (credenciales incorrectas, etc.)
          const errorCode = response.error?.code;

          // Si es un error de red, intentar de nuevo
          if (errorCode === 'NETWORK_ERROR' || errorCode === 'TIMEOUT') {
            lastError = response.error?.message || 'Error de conexión';
            if (attempt < maxRetries) {
              // Esperar antes de reintentar (500ms, 1000ms)
              await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 500));
              continue;
            }
          } else {
            // Error de autenticación, no reintentar
            setAuth(prev => ({
              ...prev,
              isLoading: false,
              error: response.error?.message || 'Credenciales incorrectas',
              initialized: true,
            }));
            return;
          }
        }
      } catch (error) {
        console.error(`Error al iniciar sesión (intento ${attempt + 1}):`, error);
        lastError = 'Error al iniciar sesión. Inténtalo de nuevo.';
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 500));
          continue;
        }
      }
    }

    // Si llegamos aquí, todos los reintentos fallaron
    setAuth(prev => ({
      ...prev,
      isLoading: false,
      error: lastError || 'No se pudo conectar con el servidor. Verifica tu conexión a internet.',
      initialized: true,
    }));
  };
  
  // Función para registrarse (solo funciona en el navegador)
  const register = async (name: string, email: string, password: string) => {
    if (!isBrowser) return;
    
    setAuth(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await apiManager.auth.register({ name, email, password });
      
      if (response.success && response.data) {
        setAuth({
          isAuthenticated: true,
          user: response.data.user,
          token: response.data.token,
          isLoading: false,
          error: null,
          initialized: true,
        });
      } else {
        setAuth(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: response.error?.message || 'Error al registrarse',
          initialized: true,
        }));
      }
    } catch (error) {
      console.error('Error al registrarse:', error);
      setAuth(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: 'Error al registrarse. Inténtalo de nuevo.',
        initialized: true,
      }));
    }
  };
  
  // Función para cerrar sesión
  const logout = () => {
    if (!isBrowser) return;

    // Track signed out event before clearing auth state
    if (auth.user) {
      trackSignedOut({
        user_id: auth.user.id,
        email: auth.user.email
      });
    }

    // Limpiar el token del cliente API
    apiManager.client.setAuthToken(null);

    apiManager.auth.logout();
    setAuth({
      ...initialState,
      isLoading: false,
      initialized: true,
    });
  };
  
  // Función para limpiar errores
  const clearError = () => {
    setAuth(prev => ({ ...prev, error: null }));
  };
  
  return (
    <AuthContext.Provider value={{ auth, login, register, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
}; 